/**
 * Audio Description Player
 * Props are updated based on changes from the MediaPlayer and Transcript
 */

import { h, Fragment } from "preact";
import { useCallback, useEffect, useRef } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { useRecordedURL } from "../../helpers/routes";
import { useVideoTime } from "../../video-player-integration/useVideoTime";
import { PlayPauseData } from "../../../types/rootDataTypes";
import {
  pauseVideo,
  playVideo,
  timerRefreshInterval,
  videoPlaybackRate,
  videoSeekPosition,
  videoState,
} from "../../video-player-integration/video-player";
import { getTimelineSegmentIndex } from "../state-machine/getTimelineSegmentIndex";
import { ADoff_Vx_Au_EADoff, StateFn } from "../state-machine/state-functions";

const ADPlayer = (props: {
  isAdEnabled: boolean;
  extended: PlayPauseData[] | undefined;
}) => {
  // Here we are converting our prop values into local signals so that we can have fully reactive
  // values and effects later. This is adapted from the "Storing information from previous renders"
  // technique described here:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const isAdEnabled = useSignal(props.isAdEnabled);
  if (props.isAdEnabled !== isAdEnabled.value) {
    isAdEnabled.value = props.isAdEnabled;
  }

  const extended = useSignal(props.extended);
  if (props.extended !== extended.value) {
    extended.value === props.extended;
  }

  const url = useRecordedURL("/audio_description");

  // Audio player controls
  const audioPlayer = useRef<HTMLMediaElement>(null);
  const playAudio = useCallback(() => {
    audioPlayer.current?.play();
  }, []);
  const pauseAudio = useCallback(() => {
    audioPlayer.current?.pause();
  }, []);
  const seekAudioTo = useCallback((newTime: number) => {
    if (!audioPlayer.current) return;
    audioPlayer.current.currentTime = newTime;
  }, []);
  const setAudioPlaybackRate = useCallback((newRate: number) => {
    if (!audioPlayer.current) return;
    audioPlayer.current.playbackRate = newRate;
  }, []);

  // "Segments" refer to the discretization of the video and audio timelines into intervals whose
  // width is equal to the timer refresh interval. See the implementation of
  // `getTimelineSegmentIndex` for more details.
  const pausePointSegments = useComputed<number[]>(() => {
    if (!extended.value) return [];
    return extended.value.map(({ pause }) => getTimelineSegmentIndex(pause));
  });
  const playPointSegments = useComputed<number[]>(() => {
    if (!extended.value) return [];
    return extended.value.map(({ play }) => getTimelineSegmentIndex(play));
  });

  // These values are used exclusively for the debug visualizer.
  const _debug_videoTime = useSignal(0);
  const _debug_audioTime = useSignal(0);

  const stateFn = useRef<StateFn>(ADoff_Vx_Au_EADoff);

  /**
   * All of the EAD logic lives in this function. On a regular interval, the video player will emit
   * its current video time; we call this a "frame". In each frame we invoke the state machine to
   * update the ADPlayer appropriately.
   */
  const frameHandler = useCallback(async (currentVideoTime: number) => {
    _debug_videoTime.value = currentVideoTime;
    const currentAudioTime = audioPlayer.current?.currentTime ?? 0;
    _debug_audioTime.value = currentAudioTime;

    // The state function will return the next state function to use on the next frame.
    stateFn.current = stateFn.current(
      {
        videoTime: currentVideoTime,
        audioTime: currentAudioTime,
        pausePointSegments: pausePointSegments.value,
        playPointSegments: playPointSegments.value,
        extended: extended.value ?? [],
        isAdEnabled: isAdEnabled.value,
        videoState: videoState.value,
        videoSeekPosition: videoSeekPosition.value,
      },
      {
        playAudio,
        pauseAudio,
        seekAudioTo,
        playVideo: playVideo.value,
        pauseVideo: pauseVideo.value,
        resetVideoSeekPosition: () => {
          videoSeekPosition.value = null;
        },
      }
    );
  }, []);
  useVideoTime(frameHandler);

  // This signal effect watches for changes to the video playback rate and updates the audio
  // playback rate to match.
  useSignalEffect(() => {
    setAudioPlaybackRate(videoPlaybackRate.value);
  });

  return (
    <>
      <audio
        ref={audioPlayer}
        src={url.toString()}
        muted={!isAdEnabled.value}
        data-testid="ad-player"
      />
      <DebugVisualizer
        videoTime={_debug_videoTime.value}
        audioTime={_debug_audioTime.value}
      />
    </>
  );
};

function DebugVisualizer({
  videoTime,
  audioTime,
}: {
  videoTime: number;
  audioTime: number;
}) {
  // The debug visualizer appears in the browser when enabled. To enable it, type this into the
  // browser console:
  // window.__3PLAY_DEBUG_AUDIO_DESCRIPTION__(true);
  // Call again with `false` to disable.
  const debugVisible = useSignal(false);
  useEffect(() => {
    window.__3PLAY_DEBUG_AUDIO_DESCRIPTION__ = (enable: boolean) => {
      debugVisible.value = enable;
    };
    return () => {
      delete window.__3PLAY_DEBUG_AUDIO_DESCRIPTION__;
    };
  }, []);

  if (!debugVisible.value) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 30,
        right: 200,
        border: "4px solid #f72798",
        backgroundColor: "#1c1c1c",
        color: "#eeeeee",
        padding: 12,
        fontFamily: "monospace",
      }}
    >
      <table>
        <tr>
          <td style={{ paddingRight: 8 }}>Current video time:</td>
          <td>{videoTime.toFixed(2).padStart(5, "0")}</td>
        </tr>
        <tr>
          <td style={{ paddingRight: 8 }}>Current audio time:</td>
          <td>{audioTime.toFixed(2).padStart(5, "0")}</td>
        </tr>
        <tr>
          <td style={{ paddingRight: 8 }}>Refresh interval:</td>
          <td>{timerRefreshInterval.value}</td>
        </tr>
      </table>
    </div>
  );
}

export default ADPlayer;
